import { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Box,
  Stack,
  Typography,
  TextField,
  MenuItem,
  Button,
  CircularProgress,
  ButtonGroup,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  Alert,
} from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import AddLinkIcon from '@mui/icons-material/AddLink';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import BoltIcon from '@mui/icons-material/Bolt';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import { PageWithTitle } from 'src/Common/components';
import { PairTable } from './components/PairTable';
import { ManualPairDialog } from './components/ManualPairDialog';
import { AutoMapEventDialog } from './components/AutoMapEventDialog';
import { EventAgentRunDrawer } from './components/EventAgentRunDrawer';
import { RejectPairDialog } from './components/RejectPairDialog';
import { MatchingHistoryTab } from './components/MatchingHistoryTab';
import { RunSelector } from './components/RunSelector';
import {
  CategoryTabs,
  HISTORY_TAB,
  ALL_TAB,
  MapperTabValue,
} from './components/CategoryTabs';
import { SubcategoryChips } from './components/SubcategoryChips';
import { PredictionCategory } from '../Predictions/constants/categories';
import {
  usePairs,
  usePairStatsByCategory,
  useConfirmPair,
  useManualConfirmPair,
  useRunMatching,
  useFetchMarkets,
  useFetchProgress,
  useMatcherSchedule,
  useToggleMatcherSchedule,
} from './api/usePairMapper';

const MATCH_TYPES = [
  { value: '', label: 'All' },
  { value: 'manual', label: 'Manual' },
  { value: 'deterministic', label: 'Deterministic' },
  { value: 'ai_confirmed', label: 'AI Confirmed' },
  { value: 'keyword_fallback', label: 'Keyword Fallback' },
  { value: 'ai_pending', label: 'AI Pending (legacy)' },
  { value: 'keyword_pending', label: 'KW Pending (legacy)' },
];

const STATUSES = [
  { value: '', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'paused', label: 'Paused' },
  { value: 'expired', label: 'Expired' },
];

const ACTIVE_TAB_KEY = 'predictionMapper.activeTab';

function readInitialTab(): MapperTabValue {
  try {
    const stored = window.localStorage.getItem(ACTIVE_TAB_KEY);
    if (stored) return stored as MapperTabValue;
  } catch {
    /* SSR / disabled storage — fall through */
  }
  return ALL_TAB;
}

export const PredictionMapperPage = () => {
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [status, setStatus] = useState('');
  const [matchedBy, setMatchedBy] = useState('');
  const [search, setSearch] = useState('');
  const [manualPairOpen, setManualPairOpen] = useState(false);
  const [autoMapOpen, setAutoMapOpen] = useState(false);
  const [agentRunId, setAgentRunId] = useState<string | null>(null);
  const [rejectPairId, setRejectPairId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<MapperTabValue>(readInitialTab);
  const [activeSubcategory, setActiveSubcategory] = useState<string | null>(
    null,
  );
  const [runId, setRunId] = useState<string | null>(null);
  const [fetchSnack, setFetchSnack] = useState<{
    severity: 'info' | 'success' | 'error';
    message: string;
  } | null>(null);
  // Set at Fetch click; the polling hook watches recent runs newer than this
  // and reports per-platform fetched/new once each lambda lands.
  const [fetchBaselineMs, setFetchBaselineMs] = useState<number | null>(null);

  // Persist tab choice so the user lands back where they left off. Wrapped in
  // try/catch since localStorage can throw in private-browsing or SSR.
  useEffect(() => {
    try {
      window.localStorage.setItem(ACTIVE_TAB_KEY, activeTab);
    } catch {
      /* noop */
    }
  }, [activeTab]);

  // Clear subcategory when switching category — `nfl` makes no sense under
  // Crypto, and stale state would silently filter every row out.
  useEffect(() => {
    setActiveSubcategory(null);
    setPage(0);
  }, [activeTab, runId]);

  const isCategoryTab = activeTab !== HISTORY_TAB;
  const isAllTab = activeTab === ALL_TAB;
  // ALL_TAB browses the whole pair collection (no category filter), sorted by
  // createdAt desc — the service already sorts that way by default.
  const activeCategory =
    isCategoryTab && !isAllTab ? (activeTab as PredictionCategory) : null;

  const { data: stats } = usePairStatsByCategory(runId);

  const { data, isLoading } = usePairs({
    status: status || undefined,
    matchedBy: matchedBy || undefined,
    search: search || undefined,
    category: activeCategory ?? undefined,
    subcategory: activeSubcategory ?? undefined,
    runId: runId ?? undefined,
    limit: pageSize,
    offset: page * pageSize,
  });

  const confirmMutation = useConfirmPair();
  const manualConfirmMutation = useManualConfirmPair();
  const matchMutation = useRunMatching();
  const fetchMutation = useFetchMarkets();
  const fetchProgress = useFetchProgress(fetchBaselineMs);
  const scheduleQuery = useMatcherSchedule();
  const toggleSchedule = useToggleMatcherSchedule();

  // Live message reflecting what the polling hook has seen so far.
  const fetchProgressMsg = useMemo(() => {
    const fmt = (n?: number | null) =>
      n == null ? '…' : n.toLocaleString('en-US');
    const poly = fetchProgress.polymarket;
    const kalshi = fetchProgress.kalshi;
    const polyStr = poly
      ? `Polymarket ✓ ${fmt(poly.marketsFetched)} fetched · ${fmt(poly.newMarketsFetched)} new`
      : 'Polymarket …';
    const kalshiStr = kalshi
      ? `Kalshi ✓ ${fmt(kalshi.marketsFetched)} fetched · ${fmt(kalshi.newMarketsFetched)} new`
      : 'Kalshi …';
    return `${polyStr}  ·  ${kalshiStr}`;
  }, [fetchProgress.polymarket, fetchProgress.kalshi]);

  // While polling: keep the Snackbar in sync with the live message.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    setFetchSnack({ severity: 'info', message: fetchProgressMsg });
  }, [fetchBaselineMs, fetchProgressMsg]);

  // Both platforms reported → success terminal state, stop polling.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    if (!fetchProgress.done) return;
    setFetchSnack({ severity: 'success', message: fetchProgressMsg });
    setFetchBaselineMs(null);
  }, [fetchBaselineMs, fetchProgress.done, fetchProgressMsg]);

  // Safety timeout: lambda fetch can take longer than 60s on a full run; stop
  // polling so the page doesn't sit on a stale "Fetching…" message forever.
  // The user can still open Run History to watch progress past this point.
  useEffect(() => {
    if (fetchBaselineMs === null) return;
    const id = setTimeout(() => {
      setFetchBaselineMs(null);
      setFetchSnack((prev) =>
        prev?.severity === 'success'
          ? prev
          : {
              severity: 'info',
              message:
                'Fetch still running — open Run History to watch the per-platform counts as each lambda completes.',
            },
      );
    }, 90_000);
    return () => clearTimeout(id);
  }, [fetchBaselineMs]);

  const isMatchRunning = matchMutation.isLoading;
  const scheduleEnabled = scheduleQuery.data?.enabled ?? false;
  const scheduleLoading = scheduleQuery.isLoading || toggleSchedule.isLoading;

  const handleConfirm = useCallback(
    (id: string) => confirmMutation.mutate(id),
    [confirmMutation],
  );

  const handleManualConfirm = useCallback(
    (id: string) => manualConfirmMutation.mutate(id),
    [manualConfirmMutation],
  );

  // Reason-gated unmatch: the dialog handles a plain reject AND, when the
  // operator supplies a reason, dispatches a fix-match agent run.
  const handleReject = useCallback((id: string) => setRejectPairId(id), []);

  const rejectingPair = data?.pairs.find((p) => p._id === rejectPairId);

  return (
    <PageWithTitle>
      <Box px={2} py={1}>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6" sx={{ color: 'white' }}>
            Prediction Pair Mapper
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Tooltip
              title={
                scheduleEnabled
                  ? `Auto-run ON — polymarket fires at :00, kalshi at :30. Toggle OFF disables both EventBridge schedules AND drops reserved concurrency to 0 (drains in-flight workers via their own 600s timeout, no new fan-out).`
                  : `Auto-run OFF — both schedules DISABLED, reserved concurrency 0. Toggle ON re-arms schedules + restores concurrency to 6. Manual RUN ALL / FULL RUN buttons still work regardless.`
              }
            >
              <FormControlLabel
                sx={{ color: 'white', mr: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={scheduleEnabled}
                    onChange={(e) => toggleSchedule.mutate(e.target.checked)}
                    disabled={scheduleLoading}
                    color="success"
                  />
                }
                label={
                  scheduleLoading ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    `Auto-run ${scheduleEnabled ? 'ON' : 'OFF'}`
                  )
                }
              />
            </Tooltip>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AddLinkIcon />}
              onClick={() => setManualPairOpen(true)}
            >
              Manual Pair
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={<AutoFixHighIcon />}
              onClick={() => setAutoMapOpen(true)}
            >
              Auto-Map Event
            </Button>
            <Button
              variant="outlined"
              size="small"
              startIcon={
                fetchMutation.isLoading ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <CloudDownloadIcon />
                )
              }
              onClick={() => {
                // Capture baseline BEFORE invoking — the polling hook ignores
                // any pre-existing runs older than this. If the lambda invoke
                // itself errors, we clear the baseline to stop polling.
                const clickedAt = Date.now();
                setFetchBaselineMs(clickedAt);
                setFetchSnack({
                  severity: 'info',
                  message:
                    'Fetch started for Polymarket + Kalshi — waiting for each lambda…',
                });
                fetchMutation.mutate(undefined, {
                  onError: (err: any) => {
                    setFetchBaselineMs(null);
                    setFetchSnack({
                      severity: 'error',
                      message:
                        'Fetch failed: ' +
                        (err?.response?.data?.message ||
                          err?.message ||
                          'unknown error'),
                    });
                  },
                });
              }}
              disabled={fetchMutation.isLoading || isMatchRunning}
            >
              Fetch Markets
            </Button>

            <ButtonGroup
              variant="contained"
              size="small"
              disabled={isMatchRunning}
            >
              <Tooltip title="Tier 1 only: deterministic matching (free, no AI)">
                <Button
                  startIcon={
                    isMatchRunning ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <BoltIcon />
                    )
                  }
                  onClick={() => matchMutation.mutate({ tiers: 'deterministic' })}
                >
                  Deterministic
                </Button>
              </Tooltip>
              <Tooltip title="Tier 1 + Tier 2: deterministic + AI confirmation">
                <Button
                  startIcon={<SmartToyIcon />}
                  onClick={() =>
                    matchMutation.mutate({ tiers: 'deterministic,ai_confirmed' })
                  }
                >
                  + AI
                </Button>
              </Tooltip>
              <Tooltip title="Tier 3 only: keyword matching with AI">
                <Button
                  startIcon={<ManageSearchIcon />}
                  onClick={() =>
                    matchMutation.mutate({ tiers: 'keyword_fallback' })
                  }
                >
                  Keywords
                </Button>
              </Tooltip>
            </ButtonGroup>

            <Button
              variant="contained"
              size="small"
              startIcon={
                isMatchRunning ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => matchMutation.mutate({})}
              disabled={isMatchRunning}
            >
              Run All
            </Button>
            <Button
              variant="contained"
              size="small"
              color="warning"
              startIcon={
                isMatchRunning ? (
                  <CircularProgress size={16} color="inherit" />
                ) : (
                  <PlayArrowIcon />
                )
              }
              onClick={() => {
                if (
                  confirm(
                    'Full run: fetch all markets, match all unpaired with all tiers. Continue?',
                  )
                ) {
                  matchMutation.mutate({ fullRun: true });
                }
              }}
              disabled={isMatchRunning}
            >
              Full Run
            </Button>
          </Stack>
        </Stack>

        <Stack
          direction="row"
          spacing={2}
          alignItems="center"
          mb={1}
          sx={{ color: 'white' }}
        >
          <RunSelector value={runId} onChange={setRunId} />
          <Typography variant="caption" sx={{ opacity: 0.6 }}>
            {runId
              ? 'Showing pairs from the selected run only.'
              : 'Showing all pairs across every run.'}
          </Typography>
        </Stack>

        <CategoryTabs
          value={activeTab}
          onChange={setActiveTab}
          stats={stats}
        />

        {isCategoryTab ? (
          <Box mt={2}>
            {activeCategory && (
              <SubcategoryChips
                category={activeCategory}
                value={activeSubcategory}
                onChange={setActiveSubcategory}
                stats={stats}
              />
            )}

            <Stack direction="row" spacing={2} mb={2}>
              <TextField
                size="small"
                label="Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 250 }}
              />
              <TextField
                size="small"
                label="Status"
                select
                value={status}
                onChange={(e) => {
                  setStatus(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 140 }}
              >
                {STATUSES.map((s) => (
                  <MenuItem key={s.value} value={s.value}>
                    {s.label}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                size="small"
                label="Match Type"
                select
                value={matchedBy}
                onChange={(e) => {
                  setMatchedBy(e.target.value);
                  setPage(0);
                }}
                sx={{ width: 180 }}
              >
                {MATCH_TYPES.map((m) => (
                  <MenuItem key={m.value} value={m.value}>
                    {m.label}
                  </MenuItem>
                ))}
              </TextField>
            </Stack>

            <PairTable
              pairs={data?.pairs || []}
              total={data?.total || 0}
              page={page}
              pageSize={pageSize}
              onPageChange={setPage}
              onPageSizeChange={(size) => {
                setPageSize(size);
                setPage(0);
              }}
              isLoading={isLoading}
              onConfirm={handleConfirm}
              onManualConfirm={handleManualConfirm}
              onReject={handleReject}
            />
          </Box>
        ) : (
          <Box mt={2}>
            <MatchingHistoryTab
              onViewRunPairs={(nextRunId, category) => {
                // Jump from a run's summary into the scoped browse view:
                // pin the Run dropdown to that run and land on its largest
                // category tab. The effect above clears subcategory + page.
                setRunId(nextRunId);
                setActiveTab(category);
              }}
            />
          </Box>
        )}
      </Box>
      <ManualPairDialog
        open={manualPairOpen}
        onClose={() => setManualPairOpen(false)}
      />
      <AutoMapEventDialog
        open={autoMapOpen}
        onClose={() => setAutoMapOpen(false)}
        onRunStarted={(runId) => setAgentRunId(runId)}
      />
      <EventAgentRunDrawer
        runId={agentRunId}
        onClose={() => setAgentRunId(null)}
      />
      <RejectPairDialog
        pairId={rejectPairId}
        pairName={rejectingPair?.name}
        onClose={() => setRejectPairId(null)}
        onFixMatchStarted={(runId) => setAgentRunId(runId)}
      />
      <Snackbar
        open={!!fetchSnack}
        // While polling, keep it pinned so the live counts can update.
        // After polling ends, auto-hide success/info; errors require a click.
        autoHideDuration={
          fetchBaselineMs !== null
            ? null
            : fetchSnack?.severity === 'error'
              ? null
              : 8000
        }
        onClose={(_, reason) => {
          // Don't auto-dismiss on click-away while we're still polling.
          if (reason === 'clickaway' && fetchBaselineMs !== null) return;
          setFetchSnack(null);
        }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {fetchSnack ? (
          <Alert
            severity={fetchSnack.severity}
            onClose={() => setFetchSnack(null)}
            variant="filled"
            sx={{ maxWidth: 480 }}
          >
            {fetchSnack.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </PageWithTitle>
  );
};
